import React, { useRef, useEffect, useCallback, useState } from "react";
import clsx from "clsx";
import { CLASSES, MQ_MAX_WIDTH_PORTRAIT } from "@excalidraw/common";
import { gsap } from "gsap";

import { useAtom } from "../editor-jotai";

import { t } from "../i18n";

import { Island } from "./Island";
import { isLibraryMenuOpenAtom } from "./LibraryMenu";
import { PremiumLibraryMenu } from "./PremiumLibrary/PremiumLibraryMenu";
import { ToolButton } from "./ToolButton";
import { TextField } from "./TextField";
import { CloseIcon, searchIcon } from "./icons";
import { useDevice } from "./App";
import { MobileBottomSheet } from "./MobileBottomSheet";
import "./FloatingLibraryPanel.scss";

type FloatingLibraryPanelProps = {
  appState: any;
  onClose: () => void;
  isOpen: boolean;
};

export const FloatingLibraryPanel = ({
  appState,
  onClose,
  isOpen,
}: FloatingLibraryPanelProps) => {
  const device = useDevice();
  const isMobile =
    device.editor.isMobile && window.innerWidth <= MQ_MAX_WIDTH_PORTRAIT;
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Desktop-only refs for GSAP animations
  const panelRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<GSAPTimeline | null>(null);
  const isClosingRef = useRef(false);

  // Ensure old library menu atom is always closed when floating panel is used
  const [isOldLibraryMenuOpen, setIsOldLibraryMenuOpen] = useAtom(
    isLibraryMenuOpenAtom,
  );

  // Debug logging and conflict prevention
  useEffect(() => {
    if (isOpen) {
      // CRITICAL: Always close the old library menu when floating panel opens
      if (isOldLibraryMenuOpen) {
        setIsOldLibraryMenuOpen(false);
      }
    }
  }, [
    isOpen,
    isMobile,
    device.editor.isMobile,
    isOldLibraryMenuOpen,
    setIsOldLibraryMenuOpen,
  ]);

  // Safety check: if old library menu opens, close this floating panel
  useEffect(() => {
    if (isOldLibraryMenuOpen && isOpen) {
      onClose();
    }
  }, [isOldLibraryMenuOpen, isOpen, onClose]);

  // Simple close handler for mobile
  const handleMobileClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Desktop GSAP functions - clean implementation
  const getLibraryButtonPosition = useCallback(() => {
    const libraryButton = document.querySelector(
      '[data-testid="floating-toolbar-library"]',
    );
    if (!libraryButton) {
      return {
        left: window.innerWidth - 120,
        top: window.innerHeight - 120,
        width: 80,
        height: 80,
      };
    }

    const rect = libraryButton.getBoundingClientRect();
    return {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    };
  }, []);

  const initializeDesktopPanel = useCallback(() => {
    if (!overlayRef.current || !contentRef.current || !closeButtonRef.current) {
      return;
    }

    const buttonPos = getLibraryButtonPosition();

    // Set initial position and size to match library button
    gsap.set(overlayRef.current, {
      position: "fixed",
      left: buttonPos.left,
      top: buttonPos.top,
      width: buttonPos.width,
      height: buttonPos.height,
      borderRadius: "1rem",
      background: "var(--island-bg-color)",
      zIndex: 10,
      opacity: 1,
      boxShadow: "none",
    });

    // Initially hide content and close button
    gsap.set(contentRef.current, {
      opacity: 0,
    });

    gsap.set(closeButtonRef.current, {
      opacity: 0,
      scale: 0,
    });
  }, [getLibraryButtonPosition]);

  const expandDesktopPanel = useCallback(() => {
    if (!overlayRef.current || !contentRef.current || !closeButtonRef.current) {
      return;
    }

    // Cancel any existing animation
    if (animationRef.current) {
      animationRef.current.kill();
    }

    const tl = gsap.timeline({
      onComplete: () => {
        // Ensure content is interactive after animation
        if (contentRef.current) {
          contentRef.current.style.pointerEvents = "auto";
        }
      },
    });

    // Calculate target dimensions
    const targetWidth = 240;
    const targetHeight = window.innerHeight - 96; // 80px top + 16px bottom
    const targetLeft = 16;
    const targetTop = 80;

    // Phase 1: Hide library button
    const libraryButton = document.querySelector(
      '[data-testid="floating-toolbar-library"]',
    );
    if (libraryButton) {
      tl.to(libraryButton, {
        duration: 0.12,
        opacity: 0,
        ease: "cubic-bezier(0.165, 0.84, 0.44, 1)", // Material Design easing
        onComplete: () => {
          (libraryButton as HTMLElement).style.pointerEvents = "none";
        },
      });
    }

    // Phase 2: Slide to left with final width
    tl.to(
      overlayRef.current,
      {
        duration: 0.15,
        width: targetWidth,
        left: targetLeft,
        ease: "cubic-bezier(0.165, 0.84, 0.44, 1)",
      },
      "+=0.03",
    )
      // Phase 3: Expand to full height
      .to(
        overlayRef.current,
        {
          duration: 0.25,
          height: targetHeight,
          top: targetTop,
          borderRadius: "0.75rem",
          boxShadow:
            "0 4px 16px rgba(0, 0, 0, 0.04), 0 1px 4px rgba(0, 0, 0, 0.02)",
          ease: "cubic-bezier(0.165, 0.84, 0.44, 1)",
        },
        "-=0.03",
      )
      // Phase 4: Fade in content
      .to(
        contentRef.current,
        {
          duration: 0.18,
          opacity: 1,
          ease: "cubic-bezier(0.165, 0.84, 0.44, 1)",
        },
        "-=0.12",
      )
      // Phase 5: Scale in close button
      .to(
        closeButtonRef.current,
        {
          duration: 0.12,
          opacity: 1,
          scale: 1,
          ease: "cubic-bezier(0.165, 0.84, 0.44, 1)",
        },
        "-=0.06",
      );

    animationRef.current = tl;
  }, []);

  const collapseDesktopPanel = useCallback(() => {
    if (
      !overlayRef.current ||
      !contentRef.current ||
      !closeButtonRef.current ||
      isClosingRef.current
    ) {
      return;
    }

    // Prevent multiple close attempts
    isClosingRef.current = true;

    // Cancel any existing animation
    if (animationRef.current) {
      animationRef.current.kill();
    }

    const buttonPos = getLibraryButtonPosition();
    const tl = gsap.timeline();

    // Disable interaction during collapse
    if (contentRef.current) {
      contentRef.current.style.pointerEvents = "none";
    }

    // Phase 1: Hide close button
    tl.to(closeButtonRef.current, {
      duration: 0.12,
      opacity: 0,
      scale: 0.8,
      ease: "power4.out",
    })
      // Phase 2: Hide content
      .to(
        contentRef.current,
        {
          duration: 0.12,
          opacity: 0,
          ease: "power4.out",
        },
        "-=0.03",
      )
      // Phase 3: Collapse to button position
      .to(
        overlayRef.current,
        {
          duration: 0.25,
          width: buttonPos.width,
          height: buttonPos.height,
          left: buttonPos.left,
          top: buttonPos.top,
          borderRadius: "1rem",
          ease: "power4.out",
        },
        "-=0.09",
      );

    // Phase 4: Show library button again
    const libraryButton = document.querySelector(
      '[data-testid="floating-toolbar-library"]',
    );
    if (libraryButton) {
      tl.to(
        libraryButton,
        {
          duration: 0.18,
          opacity: 1,
          ease: "power4.out",
        },
        "-=0.06",
      );
    }

    // Phase 5: Final fade out and cleanup
    tl.to(overlayRef.current, {
      duration: 0.08,
      opacity: 0,
      onComplete: () => {
        // Re-enable library button
        if (libraryButton) {
          (libraryButton as HTMLElement).style.pointerEvents = "auto";
        }
        onClose();
        // Reset closing flag
        setTimeout(() => {
          isClosingRef.current = false;
        }, 300);
      },
    });

    animationRef.current = tl;
  }, [getLibraryButtonPosition, onClose]);

  // Handle desktop close
  const handleDesktopClose = useCallback(() => {
    collapseDesktopPanel();
  }, [collapseDesktopPanel]);

  // Handle open/close state changes
  useEffect(() => {
    if (isOpen && !isClosingRef.current) {
      isClosingRef.current = false;
      if (!isMobile) {
        initializeDesktopPanel();
        requestAnimationFrame(() => {
          expandDesktopPanel();
        });
      }
    }
  }, [isOpen, isMobile, initializeDesktopPanel, expandDesktopPanel]);

  // Handle responsive changes - close panel when crossing mobile/desktop boundary
  useEffect(() => {
    const handleResize = () => {
      const currentIsMobile =
        device.editor.isMobile && window.innerWidth <= MQ_MAX_WIDTH_PORTRAIT;

      // If we crossed the mobile/desktop boundary and panel is open, close it
      if (isOpen && currentIsMobile !== isMobile) {
        onClose();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isOpen, isMobile, device.editor.isMobile, onClose]);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        animationRef.current.kill();
      }
    };
  }, []);

  if (!isOpen) {
    return null;
  }

  // Mobile/Tablet: Use MobileBottomSheet
  if (isMobile) {
    return (
      <MobileBottomSheet
        isOpen={isOpen}
        onClose={handleMobileClose}
        className="floating-library-mobile-sheet"
      >
        <div className="floating-library-mobile-content">
          <div className="floating-library-mobile-header">
            <TextField
              ref={searchInputRef}
              value={searchQuery}
              placeholder="Search Assets"
              onChange={setSearchQuery}
              icon={searchIcon}
              selectOnRender={false}
              className="floating-library-search"
            />
          </div>
          <div className="floating-library-mobile-body">
            <PremiumLibraryMenu
              onClose={handleMobileClose}
              searchQuery={searchQuery}
            />
          </div>
        </div>
      </MobileBottomSheet>
    );
  }

  // Desktop: Clean GSAP implementation
  return (
    <div
      ref={panelRef}
      className="floating-library-panel-desktop"
      onClick={(e) => e.stopPropagation()}
    >
      <div ref={overlayRef} className="floating-library-overlay-desktop">
        <Island
          className={clsx(CLASSES.SHAPE_ACTIONS_MENU, "App-menu__left")}
          padding={0}
          style={{
            width: "100%",
            height: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            border: "none",
            padding: "0 !important",
          }}
        >
          <div ref={contentRef} className="floating-library-content-desktop">
            <div className="floating-library-header-desktop">
              <TextField
                ref={searchInputRef}
                value={searchQuery}
                placeholder="Search Assets"
                onChange={setSearchQuery}
                icon={searchIcon}
                selectOnRender={true}
                className="floating-library-search"
              />
              <div ref={closeButtonRef}>
                <ToolButton
                  type="icon"
                  className="floating-library-close"
                  onClick={handleDesktopClose}
                  aria-label={t("buttons.close")}
                  icon={CloseIcon}
                  size="medium"
                />
              </div>
            </div>
            <PremiumLibraryMenu
              onClose={handleDesktopClose}
              searchQuery={searchQuery}
            />
          </div>
        </Island>
      </div>
    </div>
  );
};
